/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : list page 
 *   Integrations : 'NA'
 *   Version : 1.0.0
*/

// custom packages
import React, { useEffect, useRef, useState } from 'react';

// custom components
import { APIService } from '../../Service';
import { Chip, Loader } from '../../Components/Others';
import { CustomSearchInput } from '../../Components/FormElements';
import EmptyScreen from '../../Components/Others/empty-screen';
import { StatisticCard } from '../../Components/Card';
import { PrimaryButton, SecondaryButton } from '../../Components/Buttons';
import { toast } from 'react-toastify';

// custom style 
import style from "../../Style/Component/customer.module.scss";

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `₹${Number(value).toLocaleString('en-IN')}`;
};

const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toLocaleString('en-IN');
};

const formatLabel = (value) => {
    if (!value) return '-';
    return String(value)
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const getThresholdKey = (threshold, index = 0) => {
    if (!threshold || typeof threshold !== 'object') return `threshold-${index}`;
    if (threshold.type === 'sip') return `sip-${threshold.frequency || index}`;
    return `${threshold.type || 'threshold'}-${index}`;
};

const getThresholdTitle = (threshold) => {
    if (!threshold || typeof threshold !== 'object') return '-';
    if (threshold.type === 'withdrawal') return 'Withdrawal';
    return formatLabel(threshold.type);
};

const formatThresholdValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    if (key.includes('amount')) return formatCurrency(value);
    return formatNumber(value);
};

const getFpDataJson = (data) => JSON.stringify(data, null, 2);

const getFpDataFileName = (data) => {
    const schemeName = data?.mf_scheme?.name || 'fund-scheme';
    const sanitizedName = schemeName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const isin = data?.isin || 'data';
    return `${sanitizedName}-${isin}.json`;
};

const ThresholdRow = ({ label, value }) => (
    <div className='d-flex justify-content-between align-items-center gap-2 py-1'>
        <p className='mb-0 e-text-taupe-gray e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
            {label}
        </p>
        <p className='mb-0 e-text-smoky-black e-primary-font e-font-14 e-font-weight-600 line-height-18px text-end'>
            {value}
        </p>
    </div>
);

const ThresholdCard = ({ threshold }) => {
    if (!threshold || typeof threshold !== 'object') {
        return null;
    }

    const title = getThresholdTitle(threshold);
    const dynamicEntries = Object.entries(threshold).filter(([key]) => key !== 'type' && key !== 'dates');

    return (
        <div className='e-bg-white e-border-0-5px-taupe-gray border-radius-16 p-3 h-100'>
            <p className='mb-3 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                {title}
            </p>

            {threshold.type === 'withdrawal' ? (
                <>
                    <ThresholdRow label="Minimum Amount" value={formatCurrency(threshold.amount_min)} />
                    <ThresholdRow label="Maximum Amount" value={formatCurrency(threshold.amount_max)} />
                    <ThresholdRow label="Amount Multiples" value={formatCurrency(threshold.amount_multiples)} />

                    <hr className='my-2 opacity-25' />

                    <ThresholdRow label="Minimum Units" value={formatNumber(threshold.units_min)} />
                    <ThresholdRow label="Maximum Units" value={formatNumber(threshold.units_max)} />
                    <ThresholdRow label="Units Multiples" value={formatNumber(threshold.units_multiples)} />
                </>
            ) : (
                <>
                    {dynamicEntries.map(([key, value]) => (
                        <ThresholdRow
                            key={key}
                            label={formatLabel(key)}
                            value={formatThresholdValue(key, value)}
                        />
                    ))}

                    {'dates' in threshold && (
                        <>
                            <p className='mb-2 mt-3 e-text-taupe-gray e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
                                {formatLabel('dates')}
                            </p>

                            {threshold.dates?.length > 0 ? (
                                <div className='d-flex flex-wrap gap-1'>
                                    {threshold.dates.map((date) => (
                                        <div key={date}>
                                            <div className='e-bg-magnolia-extra-dark w-24px h-24px e-font-10 e-font-weight-600 line-height-10px e-primary-font e-text-blue-violet rounded d-flex align-items-center justify-content-center p-1'>
                                                {date}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='e-bg-magnolia-extra-dark rounded p-2 text-center'>
                                    <p className='mb-0 e-text-blue-violet e-primary-font e-font-12 e-font-weight-400 line-height-16px'>
                                        No specific date selection.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};


const FundSchemeList = () => {

    const scrollRef = useRef(null);
    const hasFetchedRef = useRef(false);

    const [fpData, setFpData] = useState({});
    const [loader, setLoader] = useState(true);
    const [shortName, setShortName] = useState("");

    const [searchValue, setSearchValue] = useState("");

    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        _getFpData();
    }, []);

    useEffect(() => {
        if (fpData?.mf_scheme?.name) {
            const words = fpData.mf_scheme.name.split(' ').filter(Boolean);
            const initials = words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
            setShortName(initials);
        }
    }, [fpData]);


    // API - Get group list
    const _getFpData = (search = searchValue) => {
        let is_post = true;
        let url = 'amc/fund-scheme/get-fp-data';
        let request = {
            "query": search || "",
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setFpData(response.data || {});
                setErrorMessage("");
            } else {
                setFpData({});
                setShortName("");
                setErrorMessage(response.message || "No data found");
            }
            setLoader(false);
        })
    };

    const _clearSearch = () => {
        setSearchValue("");
        setLoader(true);
        _getFpData("");
    };

    const _downloadJson = () => {
        const blob = new Blob([getFpDataJson(fpData)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = getFpDataFileName(fpData);
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <section className='e-section-height pb-5 e-bg-magnolia' >
            <div className='e-bg-lavender padding-52px-lr pt-2 padding-80px-bottom'>
                <div className='d-flex align-items-center justify-content-between gap-3 margin-32px-bottom '>
                    <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Fund Scheme
                    </h3>

                    <CustomSearchInput placeholder="Search with scheme name & ISIN"
                        type="text"
                        onSearch={() => {
                            _getFpData(searchValue)
                            setLoader(true);
                        }}
                        value={searchValue}
                        onKeyPress={() => {
                            _getFpData(searchValue)
                            setLoader(true);
                        }}
                        onClear={() => {
                            if (searchValue.length > 0) {
                                _clearSearch();
                            }
                        }}
                        className="w-340px"
                        handleChange={(e) => {
                            if (e.length === 0 && searchValue.length > 0) {
                                _clearSearch();
                                return;
                            }
                            setSearchValue(e);
                        }} />
                </div>
            </div>

            <div className='padding-52px-lr margin--80px-top'>
                {Object.keys(fpData).length !== 0 &&
                    <>
                        <div className={`${style.e_summary_wrapper} e-bg-american-violet border-radius-16 padding-32px-tb px-4 position-relative`}>
                            <div className='position-absolute top-32px right-24px d-flex justify-content-end'>
                              
                                <PrimaryButton
                                    name="Download JSON"
                                    paddingBlock='padding-12px-tb padding-12px-lr'
                                    onClick={_downloadJson}
                                />
                            </div>

                            <div className='d-flex gap-4 align-items-start'>
                                <div className='e-bg-blue-violet-light border-radius-64 padding-22px-lr py-4 rounded-circle w-76px h-76px d-flex flex-column justify-content-center align-items-center'>
                                    <p className='mb-0 e-text-cultured-light e-primary-font e-font-20 e-font-weight-400 line-height-20px'>
                                        {shortName}
                                    </p>
                                </div>

                                <div className='d-flex align-items-start gap-3'>
                                    <div className='d-flex flex-column gap-12px'>
                                        <p className='mb-0 e-text-platinum e-primary-font e-font-14 e-font-weight-600 line-height-18px'>
                                            {fpData?.mf_scheme?.name || '-'}
                                        </p>

                                        <p className='mb-0 e-text-platinum e-primary-font e-font-12 e-font-weight-400 line-height-18px'>
                                            MF Funds: <br />
                                            <span className='e-font-weight-600 e-font-14'>
                                                {fpData?.mf_fund?.name || '-'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className='vr e-bg-lavender-blue'></div>
                                    <div className='d-flex flex-column gap-12px'>
                                        <div className='d-flex align-items-center gap-1'>
                                            <p className='mb-0 e-text-platinum e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
                                                Status:
                                            </p>
                                            <Chip type={2}
                                                dotClass={fpData?.active ? "e-bg-green-crayola" : "e-bg-marigold"}
                                                label={fpData?.active ? "Active" : "Inactive"} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Statistic cards */}
                            <div className='row row-cols-4 row-gap-4 pt-4'>
                                <div className='col'>
                                    <StatisticCard title="ISIN:"
                                        value={fpData?.isin || "-"}
                                    />
                                </div>
                                <div className='col'>
                                    <StatisticCard title="Gateway"
                                        value={fpData?.gateway || "-"}
                                    />
                                </div>
                                <div className='col'>
                                    <StatisticCard title="IDCW Option"
                                        value={fpData?.idcw_option || "-"}
                                    />
                                </div>

                                <div className='col'>
                                    <StatisticCard title="Object"
                                        value={fpData?.object || "-"}
                                    />
                                </div>

                                <div className='col'>
                                    <StatisticCard title="Option"
                                        value={fpData?.option || "-"}
                                    />
                                </div>

                                <div className='col'>
                                    <StatisticCard title="Type"
                                        value={fpData?.type || "-"}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                }


                <div ref={scrollRef} className='e-bg-white border-radius-16 margin-20px-top p-4 '>
                    {loader === true ?
                        <Loader className="py-5 min-vh-100" />
                        :
                        Array.isArray(fpData?.thresholds) && fpData.thresholds.length > 0 ?
                            <div className='row row-cols-1 row-cols-md-2 row-gap-3 gx-3'>
                                {fpData.thresholds.map((threshold, index) => (
                                    <div className='col' key={getThresholdKey(threshold, index)}>
                                        <ThresholdCard threshold={threshold} />
                                    </div>
                                ))}
                            </div>
                            :
                            <EmptyScreen
                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                title={errorMessage || 'No results found'}
                                description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                            />
                    }
                </div>
            </div >
        </section>
    )
}

export default FundSchemeList;